import { useState } from "react"
import { useNavigate } from "react-router"
import { formatCurrency } from "../../lib/formatCurrency"
import { formatDateInTimeZone } from "../../lib/formatDateInTimeZone"
import { byCreatedAtDescending } from "../../lib/util/sort"
import { useAppDispatch, useAppSelector } from "../hooks/reduxToolkit"
import { AdvanceRequestModel, AdvanceModel, deleteAdvanceRequest } from "../reduxToolkit/advancesSlice"
import { RequireIncidentProvided } from "../wrappers/requireIncident"
import { useConfirm } from "../providers/confirmDialog"

import './advanceRequestHistory.scss'

interface AdvanceRequestRowProps {
  item: {
    created_at: string,
    advanceRequest: AdvanceRequestModel | null,
    advance: AdvanceModel | null
  },
  incidentId: string
}

function AdvanceRequestRow({ item, incidentId }: AdvanceRequestRowProps) {
  const [expanded, setExpanded] = useState(false)
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const [show, result] = useConfirm()

  const handleCancelRequest = async (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    
    if (await show({
      title: 'Cancel Advance Request',
      message: 'Are you sure you want to cancel this advance request? You can create a new request later.',
      actions: [
        {
          title: 'Cancel Request',
          style: 'danger',
          isConfirm: true
        }
      ]
    })) {
      const now = new Date().toISOString()
      dispatch(deleteAdvanceRequest({
        id: item.advanceRequest!.id,
        updated_at: now,
        deleted_at: now
      }))
      setExpanded(false)
    }
  }

  if (!item.advance) {
    return <div className='row'>
      <div className={`col-12 card advance-request-history__row`}>
        <div className="card-body">
          <div className={`advance-request-history__row-header`}>
            <div className="card-title d-flex">
              {formatDateInTimeZone(item.created_at, { format: 'MMM dd' })}
              &nbsp;-&nbsp;
              {formatCurrency(item.advanceRequest!.approved_amount ?? item.advanceRequest!.amount)}
              
              {item.advanceRequest!.approved_at ?
                <span className='badge bg-info ms-auto me-4'>Approved (not released)</span> :
                item.advanceRequest!.rejected_at ?
                  <span className='badge bg-danger ms-auto me-4'>Rejected</span> :
                  <span className='badge bg-info ms-auto me-4'>Pending</span>}
            </div>
            
            {item.advanceRequest!.approved_amount && (item.advanceRequest!.approved_amount != item.advanceRequest!.amount) &&
              <div className="text-muted">
                <small>(amount changed)</small>
              </div>}

            <button className="btn btn-link advance-request-history__row-collapse-toggle"
              aria-expanded={expanded} 
              onClick={(e) => {e.preventDefault(); e.stopPropagation(); setExpanded(!expanded) }}>
              <i className={`material-icons`}>{expanded ? 'expand_less' : 'expand_more'}</i>
            </button>
          </div>

          <div className={`advance-request-history__row-collapse collapse ${expanded && 'show'}`}>
            <div className="card-text">
              <p>Your advance request is still in progress. Please contact your plan administrator if you have any questions.</p>
              {item.advanceRequest!.approved_amount && (item.advanceRequest!.approved_amount != item.advanceRequest!.amount) &&
                <p className="text-muted">
                  Your plan administrator has chosen to advance a different amount, not the original request amount of {formatCurrency(item.advanceRequest!.amount)}.
                </p>}
              
              {!item.advanceRequest!.approved_at && !item.advanceRequest!.rejected_at && (
                <div className="mt-3 float-end">
                  <button className="btn btn-outline-danger" onClick={handleCancelRequest}>
                    Cancel Request
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  }
  
  // Funds have been released, show the advance
  return (
    <div className='row'>
      <div
        className={`col-12 card advance-request-history__row ${item.advance.repaid_at ? 'advance-request-history__row--repaid' : 'advance-request-history__row--pending'}`}
        onClick={!item.advance.repaid_at ? () => navigate(`/incidents/${incidentId}/advances/${item.advance!.id}`) : undefined}
        style={{ cursor: !item.advance.repaid_at ? 'pointer' : 'default' }}
      >
        <div className="card-body">
          <div className={`advance-request-history__row-header`}>
            <div className="card-title d-flex">
              {formatDateInTimeZone(item.created_at, { format: 'MMM dd' })}
              &nbsp;-&nbsp;
              {formatCurrency(item.advance.amount)}
              
              {item.advance.repaid_at ?
                <span className='badge bg-success ms-auto me-4'>Repaid</span> :
                item.advance.indicated_repaid_at ?
                  <span className='badge bg-info ms-auto me-4'>Awaiting Receipt of Repayment</span> :
                  <span className='badge bg-info ms-auto me-4'>Funds Sent</span>}
            </div>
            
            {item.advanceRequest?.approved_amount && (item.advanceRequest.approved_amount != item.advanceRequest.amount) &&
              <div className="text-muted">
                <small>(amount changed)</small>
              </div>}

          </div>

          <div className={`advance-request-history__row-collapse collapse ${expanded && 'show'}`}>
            <div className="card-text">
              {item.advanceRequest?.approved_amount && (item.advanceRequest.approved_amount != item.advanceRequest.amount) &&
                <p className="text-muted">
                  Your plan administrator has chosen to advance a different amount, not the original request amount of {formatCurrency(item.advanceRequest.amount)}.
                </p>}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export function AdvanceRequestHistory({ incident }: RequireIncidentProvided) {
  const advances = useAppSelector(s => s.advances.advances?.filter((a) => a.incident_id === incident.id) || [])
  const advanceRequests = useAppSelector((s) => s.advances.advanceRequests?.filter((ar) => ar.incident_id === incident.id) || [])
  
  type ListItem = (
    {
      // An advance request where funds haven't been disbursed yet
      created_at: string,
      advanceRequest: AdvanceRequestModel,
      advance: null
    } | {
      // An advance request where funds have been disbursed
      created_at: string,
      advance: AdvanceModel,
      advanceRequest: AdvanceRequestModel
    } | {
      // An advance that was given without a request
      created_at: string,
      advance: AdvanceModel,
      advanceRequest: null
    }
  )
  
  const listItems: Array<ListItem> = [
    // Any advances that were requested
    ...advanceRequests.map((ar) => ({
      created_at: ar.created_at,
      advanceRequest: ar,
      advance: advances.find((a) => a.advance_request_id === ar.id) || null
    })),
    // Any advances that were given but not requested
    ...advances.filter((a) => !a.advance_request_id).map((a) => ({
      created_at: a.created_at,
      advance: a,
      advanceRequest: null
    }))
  ]
  
  const sortedAdvances = listItems.sort(byCreatedAtDescending)
  return <div className='advance-request-history'>
    {sortedAdvances.map((item) => (
      <AdvanceRequestRow key={item.advance?.id || item.advanceRequest?.id} item={item} incidentId={incident.id} />
    ))}
  </div>
}